<template>
  <vs-popup class="popup-q-r-history" title="QR 참석자 명단 가져오기" :active.sync="showPopup">
    <div>
      <search-nav class="w-full" @handleSearch="applySearch">
        <search-date label="스캔일시" v-model="date" :readonly="!!searchDate" @input="applySearch" />
        <search-time-start-end label="출입시간" :readonly="!date" :options="{ showLabelOnlyMobile: true }" v-model="time" @input="applySearch" :enable-seconds="false" />
        <search-select label="사업장명" :options="{ items: optionFields }" v-model="region" @input="applySearch" />
        <search-keyword label="스캔 데이터" v-model="scanData" :on-click-search="applySearch" />
        <search-keyword label="부서(업체명)" v-model="userComName" :on-click-search="applySearch" />

        <div class="flex items-center gap-2 xl:w-auto">
          <span class="title">조회범위</span>
          <div class="flex-1 flex items-center gap-3">
            <vs-radio vs-name="scanType" :vs-value="true" v-model="isMyScans" @change="applySearch">나의 스캔 목록</vs-radio>
            <vs-radio vs-name="scanType" :vs-value="false" v-model="isMyScans" @change="applySearch">전체 스캔 목록</vs-radio>
          </div>
        </div>
        <search-keyword label="스캐너 사용자" v-model="scannerName" :clearable="true" :clearAndSearch="true" :on-click-search="applySearch" />
      </search-nav>

      <div class="w-full flex justify-between items-center mt-2">
        <vs-select v-model="perPage" autocomplete style="width: 120px" @change="applySearch">
          <vs-select-item v-for="i in [10, 20, 50, 100]" :key="i" :text="`${i}개씩 보기`" :value="i" />
        </vs-select>

        <div class="flex items-center gap-4">
          <!-- 선택된 인원 정보 -->
          <div class="selection-info">
            <span class="text-sm text-gray-600">
              기존 선택: <span class="font-medium text-blue-600">{{ alreadySelectedCount }}명</span> | 선택: <span class="font-medium text-green-600">{{ newlySelectedCount }}개 (사용자: {{ uniqueSelectedCount }}명)</span>
            </span>
          </div>

          <!-- 안내 메시지 -->
          <div v-if="hasAlreadySelectedItems" class="info-message">
            <i class="info-icon">ℹ️</i>
            이미 참석자명단에 등록된 사용자는 회색으로 표시되며 해제할 수 없습니다. 삭제는 참석자명단에서 직접 수행해주세요.
          </div>
        </div>
      </div>

      <div class="mt-4">
        <vs-table class="w-full mb-4" stripe :sst="true" :max-items="perPage" :data="list" :multiple="true" v-model="selected" noDataText="데이터가 없습니다">
          <template slot="thead">
            <vs-th><span class="w-full text-center">No.</span></vs-th>
            <vs-th><span class="w-full text-center">스캔일시</span></vs-th>
            <vs-th><span class="w-full text-center">스캐너 사용자</span></vs-th>
            <vs-th><span class="w-full text-center">사업장명</span></vs-th>
            <vs-th><span class="w-full text-center">장소</span></vs-th>
            <vs-th><span class="w-full text-center">부서(업체)명</span></vs-th>
            <vs-th><span class="w-full text-center">소속</span></vs-th>
            <vs-th><span class="w-full text-center">직책</span></vs-th>
            <vs-th><span class="w-full text-center">이름</span></vs-th>
            <vs-th><span class="w-full text-center">스캔 데이터</span></vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr v-for="(tr, tr_i) in data" :key="`popup-q-r-history-${tr_i}`" :data="tr" :class="{ 'already-selected': tr.isAlreadySelected }">
              <vs-td align="center">{{ total - ((loadedPage - 1) * perPage + tr_i) }}</vs-td>
              <vs-td align="center">{{ tr["스캔일자"] }} {{ tr["스캔시간"] }}</vs-td>
              <vs-td align="center">{{ tr["스캐너 사용자"] }}</vs-td>
              <vs-td align="center">{{ tr["사업장명"] }}</vs-td>
              <vs-td align="center">{{ tr["장소"] }}</vs-td>
              <vs-td align="center">{{ tr["업체명"] }}</vs-td>
              <vs-td align="center">{{ tr["소속"] }}</vs-td>
              <vs-td align="center">{{ tr["직책"] }}</vs-td>
              <vs-td align="center">{{ tr["이름"] }}</vs-td>
              <vs-td align="center">{{ tr["데이터"] }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>
      <div class="vs-con-table">
        <div class="con-pagination-table vs-table--pagination">
          <k-pagination :total="lastPage ? lastPage : 1" v-model="page" @change="onChangePage"></k-pagination>
        </div>
      </div>

      <div class="mt-8 flex justify-end gap-4">
        <vs-button color="secondary" type="border" @click="onClickClose">닫기</vs-button>
        <vs-button color="secondary" @click="onClickUse">등록</vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import HttpRequest from "@/util/HttpRequest";
import SearchNav from "@/components/nav/SearchNav.vue";
import TimeSelectorRang from "@/components/selector/TimeSelectorRang.vue";
import SearchStartEnd from "@/views/list/components/SearchStartEnd.vue";
import SearchDate from "@/views/list/components/SearchDate.vue";
import SearchSelect from "@/views/list/components/SearchSelect.vue";
import SearchKeyword from "@/views/list/components/SearchKeyword.vue";
import SearchTimeStartEnd from "@/views/list/components/SearchTimeStartEnd.vue";
import KPagination from "@/components/kPagination/kPagination.vue";

export default {
  name: "PopupQRHistory",
  components: {
    KPagination,
    SearchTimeStartEnd,
    SearchKeyword,
    SearchSelect,
    SearchDate,
    SearchStartEnd,
    TimeSelectorRang,
    SearchNav,
  },
  props: {
    value: Boolean,
    projectId: Number,
    searchDate: String,
    selectedUserIds: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    showPopup: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    projId() {
      return this.projectId || this.$store.state.project.projectId;
    },
    optionFields() {
      return [
        { value: 0, text: "전체" },
        ...this.regions.map((item) => {
          return {
            text: item.name,
            value: item.id,
          };
        }),
      ];
    },
    memberInfo() {
      return this.$store.state.member.memberInfo;
    },
    hasAlreadySelectedItems() {
      return this.list.some((item) => item.isAlreadySelected);
    },
    alreadySelectedCount() {
      return this.selectedUserIds.length;
    },
    newlySelectedCount() {
      return this.allSelectedItems.filter((item) => !item.isAlreadySelected).length;
    },
    uniqueSelectedCount() {
      // 선택된 사용자 중 user_id 를 기준으로 중복제거한 개수
      return [...new Set(this.allSelectedItems.map(item => item.user_id))].length;
    },
  },
  watch: {
    value(value) {
      if (value) {
        // 한국 시간으로 오늘 날짜
        this.date = new Date(this.searchDate).format("yyyy-MM-dd");
        this.page = 1;
        this.perPage = 10;
        this.keyword = "";
        this.allSelectedItems = []; // 팝업 열릴 때 전체 선택 항목 초기화
        this.loadHistory();
        this.loadRegionList();
      }
    },
    selected: {
      handler(newSelected) {
        // 이미 선택된 항목들이 해제되지 않도록 보호
        const alreadySelectedItems = this.list.filter((item) => item.isAlreadySelected);
        const missingItems = alreadySelectedItems.filter((item) => !newSelected.some((selected) => selected.user_id === item.user_id));

        if (missingItems.length > 0) {
          // 해제된 기존 선택 항목들을 다시 추가
          this.selected = [...newSelected, ...missingItems];
          return;
        }

        // 전체 선택된 항목들 업데이트
        this.updateAllSelectedItems(newSelected);
      },
      deep: true,
    },
  },
  data() {
    return {
      page: 1,
      loadedPage: 1,
      perPage: 20,
      lastPage: 1,
      total: 0,
      date: "",
      time: {
        startTime: "",
        endTime: "",
      },
      region: 0,
      zoneName: "",
      scanData: "",
      userComName: "",
      list: [],
      selected: [],
      allSelectedItems: [], // 전체 선택된 항목들 저장
      regions: [],
      isMyScans: true,
      scannerName: "",
    };
  },

  methods: {
    applySearch() {
      this.page = 1;
      this.loadHistory();
    },
    loadHistory() {
      const options = {
        params: {
          page: this.page,
          perPage: this.perPage,
          projectId: this.projId,
          type: "identity",
          //boardId
          //fieldId
          //postId
          //regionId
          //zoneId
          //scannerUerId
          //dateFrom
          //dateTo
          //scannerUserId
        },
      };

      if (this.scannerName) {
        options.params.scannerName = this.scannerName;
      }

      const date = this.date;
      if (date.trim()) { // date 에 스페이스가 들어가는 경우가 있어서, trim 으로 체크
        const dateFrom = new Date(date);
        let startTime = `${this.time.startTime}`.replaceAll("_", "0");
        let endTime = `${this.time.endTime}`.replaceAll("_", "0");
        if (startTime && startTime.split(":").length === 2) startTime += ":00";
        if (endTime && endTime.split(":").length === 2) endTime += ":00";
        options.params.dateFrom = dateFrom.format(`yyyy-MM-dd ${startTime || "00:00:00"}`);
        if (endTime) {
          options.params.dateTo = dateFrom.format(`yyyy-MM-dd ${endTime}`);
        } else {
          options.params.dateTo = new Date(dateFrom.setDate(dateFrom.getDate() + 1)).format("yyyy-MM-dd 00:00:00");
        }
      }

      if (this.region) {
        options.params.regionId = this.region;
      }

      if (this.zoneName) {
        options.params.zoneName = this.zoneName;
      }
      if (this.scanData) {
        options.params.data = this.scanData;
      }

      if (this.userComName) {
        options.params.userComName = this.userComName;
      }

      if (this.isMyScans && this.memberInfo) {
        options.params.scannerUserId = this.memberInfo.id;
      }

      HttpRequest.get("/api/v1/project/qr/history/list", options)
        .then((res) => {
          this.list = this.convertList(res.data);
          this.total = res.total;
          this.lastPage = res.last_page;
          this.loadedPage = this.page;
          this.restoreSelectedState();
        })
        .catch((err) => {
          console.error("QR 스캔 목록 조회 오류:", err);
        });
    },

    convertList(list) {
      return list.map((item) => {
        const scanner = item.scanner || {};
        const scannerInfo = [];
        if (scanner.department) {
          scannerInfo.push(scanner.department);
        }
        if (scanner.name) {
          scannerInfo.push(scanner.name);
        }
        if (scanner.rank) {
          scannerInfo.push(scanner.rank);
        }

        return {
          "스캔일자": this.$options.filters.date(item.created_at),
          "스캔시간": this.$options.filters.date(item.created_at, "HH:mm:ss"),
          "스캐너 사용자": scannerInfo.length > 0 ? scannerInfo.join(" ") : "-",
          "사업장명": item.region ? item.region.name : "-",
          "장소": item.zone ? item.zone.name : "-",
          "업체명": this.getCompany(item),
          "소속": item.user ? item.user.department : "-",
          "직책": item.user ? item.user.rank : "-",
          "직종": item.meta ? item.meta.직종 : "-",
          "이름": item.user ? item.user.name : "-",
          "데이터": item.data || "-",
          "user_id": item.user ? item.user.id : null,
          "isAlreadySelected": this.selectedUserIds.includes(item.user ? item.user.id : null),
          "raw": item,
        };
      });
    },

    loadRegionList() {
      const option = {
        params: {
          page: 1,
          perPage: 20,
          keyword: "", // 검색이 필요할 경우 사용,
        },
      };
      // keyword 를 사용하지 않을 경우 삭제
      if (option.params.keyword === "") {
        delete option.params.keyword;
      }

      const api = "/api/v1/region";
      HttpRequest.get(api, option)
        .then((response) => {
          const data = response.data;
          this.regions = data;
        })
        .catch((err) => {
          console.error("지역 목록 조회 오류:", err);
        });
    },

    getCompany(item) {
      let company = "";
      if (item.user && item.user.company) {
        company += item.user.company.com_name || "-";
      }
      return company;
    },

    onChangePage() {
      if (this.value) {
        this.loadHistory();
      }
    },

    onClickClose() {
      this.showPopup = false;
    },

    onClickUse() {
      // 새로 선택된 항목들만 필터링 (이미 선택되어 있던 항목들 제외)
      const newlySelected = this.allSelectedItems.filter((item) => !item.isAlreadySelected);
      this.$emit("select", newlySelected);
      this.onClickClose();
    },

    updateAllSelectedItems(newSelected) {
      // 현재 페이지의 user_id 목록
      const currentPageUserIds = this.list.map((item) => item.user_id).filter((id) => id !== null);

      // 다른 페이지에서 선택된 항목들 유지 (현재 페이지에 없는 항목들)
      const otherPageSelected = this.allSelectedItems.filter((item) => !currentPageUserIds.includes(item.user_id));

      // 현재 페이지에서 선택된 항목들과 다른 페이지 선택 항목들 병합
      this.allSelectedItems = [...otherPageSelected, ...newSelected];
    },

    restoreSelectedState() {
      // 1. 이미 선택된 항목들 (selectedUserIds 기준)
      const alreadySelectedItems = this.list.filter((item) => this.selectedUserIds.includes(item.user_id));

      // 2. 다른 페이지에서 선택된 항목들 중 현재 페이지에 있는 것들
      const otherPageSelectedInCurrentPage = this.list.filter((item) => this.allSelectedItems.some((selected) => selected.user_id === item.user_id) && !this.selectedUserIds.includes(item.user_id));

      // 3. 현재 페이지의 선택 상태 복원
      this.selected = [...alreadySelectedItems, ...otherPageSelectedInCurrentPage];
    },
  },
};
</script>

<style scoped lang="scss">
.popup-q-r-history::v-deep {
  .vs-popup {
    width: 1500px !important;
    max-width: 90% !important;
  }

  .title {
    font-weight: 500;
    line-height: 38px;
    text-align: left;
    white-space: nowrap;

    @media (max-width: 1200px) {
      width: 80px !important;
    }
  }

  // 이미 선택된 항목 스타일링
  .vs-table .already-selected {
    background-color: #f8f9fa !important;
    color: #6c757d !important;

    // 체크박스 완전히 숨기기
    .vs-checkbox {
      display: none !important;
    }

    // 첫 번째 td에 "이미 선택됨" 표시 추가
    td:first-child {
      position: relative;

      &::after {
        content: "-";
        position: absolute;
        top: 50%;
        right: 5px;
        transform: translateY(-50%);
        font-size: 11px;
        color: #6c757d;
        background: #e9ecef;
        padding: 2px 6px;
        border-radius: 3px;
        font-weight: 500;
      }
    }
  }
}

.info-message {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  color: #1976d2;

  .info-icon {
    color: #2196f3;
    margin-right: 8px;
  }
}
</style>
